import { FormEvent, useCallback, useEffect, useState } from 'react'
import { formatTaskDeadline } from '../features/campaign/public'
import { platformApi } from '../features/platform/public'

type SubmissionStage = 'draft' | 'published'

type CreatorTaskPayload = {
  campaign: { id: string; name: string; brand_name: string; timezone: string }
  creator: { full_name: string; primary_platform: string | null; primary_handle: string | null }
  task: {
    id: string
    status: string
    due_at: string | null
    assignee_campaign_creator_id: string
    requirement_version: number
    acknowledged_requirement_version: number | null
    acknowledged_at: string | null
    acknowledgement_required: boolean
    acknowledgement_satisfied: boolean
    is_overdue: boolean
  }
  deliverable: {
    id: string
    name: string
    type: string
    instructions: string | null
    brief_url: string | null
    requires_draft: boolean
    current_requirement_version: number
  }
  requirement: {
    version_number: number
    requirement: Record<string, unknown>
    acknowledgement_required: boolean
    impact_warning: string | null
    created_at: string
  } | null
  brief: {
    version_number: number
    brief: Record<string, unknown>
    effective_at: string | null
  } | null
  submission: {
    id: string
    status: string
    current_version: number
    current_stage: SubmissionStage | null
  } | null
  publication: {
    version_number: number
    url: string
    creator_note: string | null
    created_at: string
  } | null
  latest_review: {
    decision: string
    comment: string | null
    revision_due_at: string | null
    created_at: string
  } | null
}

function requirementText(payload: CreatorTaskPayload): string | null {
  const value = payload.requirement?.requirement.instructions
  return typeof value === 'string' && value.trim() ? value : payload.deliverable.instructions
}

function requirementBriefUrl(payload: CreatorTaskPayload): string | null {
  const value = payload.requirement?.requirement.brief_url
  return typeof value === 'string' && value.trim() ? value : payload.deliverable.brief_url
}

export default function CreatorPortal({ token }: { token: string }) {
  const [payload, setPayload] = useState<CreatorTaskPayload | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [kind, setKind] = useState<'link' | 'text'>('link')
  const [content, setContent] = useState('')
  const [note, setNote] = useState('')
  const [postUrl, setPostUrl] = useState('')
  const [postNote, setPostNote] = useState('')
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    const result = await platformApi.creatorPortal.getTask(token)
    if (result.error) setError(result.error.message)
    else if (!result.data) setError('Link đã hết hạn, bị thu hồi hoặc không hợp lệ.')
    else setPayload(result.data as CreatorTaskPayload)
    setLoading(false)
  }, [token])

  useEffect(() => { void load() }, [load])

  async function acknowledge() {
    setBusy(true)
    setError('')
    const result = await platformApi.creatorPortal.acknowledge(token)
    setBusy(false)
    if (result.error) setError(result.error.message)
    else {
      setMessage(`Đã xác nhận yêu cầu phiên bản ${payload?.task.requirement_version ?? ''}.`)
      await load()
    }
  }

  async function submitDraft(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError('')
    setMessage('')
    const result = await platformApi.creatorPortal.submit({
      token,
      kind,
      contentValue: content,
      creatorNote: note || null,
    })
    setBusy(false)
    if (result.error) setError(result.error.message)
    else {
      const submitted = result.data as { version_number: number }
      setMessage(`Đã nộp draft phiên bản ${submitted.version_number}.`)
      setContent('')
      setNote('')
      await load()
    }
  }

  async function publish(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError('')
    setMessage('')
    const result = await platformApi.creatorPortal.publish({
      token,
      postUrl,
      creatorNote: postNote || null,
    })
    setBusy(false)
    if (result.error) setError(result.error.message)
    else {
      const published = result.data as { version_number: number }
      setMessage(`Đã ghi nhận post link ở phiên bản ${published.version_number}.`)
      setPostUrl('')
      setPostNote('')
      await load()
    }
  }

  if (loading) return <CreatorState title="Đang mở nhiệm vụ…" />
  if (error && !payload) return <CreatorState title="Không thể mở nhiệm vụ" message={error} />
  if (!payload) return null

  const due = payload.latest_review?.revision_due_at || payload.task.due_at
  const instructions = requirementText(payload)
  const briefUrl = requirementBriefUrl(payload)
  const acknowledgementSatisfied = !payload.task.acknowledgement_required || payload.task.acknowledgement_satisfied
  const showAcknowledge = !payload.task.acknowledgement_satisfied || ['not_sent', 'sent'].includes(payload.task.status)
  const isPublishedOrClosed = ['published', 'verified', 'completed'].includes(payload.task.status)
  const canSubmitDraft = acknowledgementSatisfied
    && payload.deliverable.requires_draft
    && !['approved', 'published', 'verified', 'completed'].includes(payload.task.status)
  const canPublish = acknowledgementSatisfied && (
    payload.task.status === 'approved'
    || (!payload.deliverable.requires_draft && payload.task.status === 'acknowledged')
  )

  return (
    <main className="creator-page">
      <section className="creator-phone">
        <header className="creator-header">
          <div className="creator-brand-mark">CO</div>
          <div>
            <p className="eyebrow">{payload.campaign.brand_name}</p>
            <strong>{payload.campaign.name}</strong>
          </div>
        </header>

        <section className="creator-welcome">
          <p className="muted">Xin chào</p>
          <h1>{payload.creator.full_name}</h1>
          <p className="muted">{[payload.creator.primary_platform, payload.creator.primary_handle].filter(Boolean).join(' · ')}</p>
        </section>

        <article className="creator-card">
          <div className="creator-card-head">
            <div><p className="eyebrow">NHIỆM VỤ</p><h2>{payload.deliverable.name}</h2></div>
            <span className="status status-configured">{payload.task.status}</span>
          </div>
          <p className="muted small">
            Yêu cầu v{payload.task.requirement_version}
            {payload.brief ? ` · Brief v${payload.brief.version_number}` : ''}
            {` · ${payload.campaign.timezone}`}
          </p>
          {instructions && <p>{instructions}</p>}
          {payload.requirement?.impact_warning && <div className="notice"><strong>Yêu cầu đã thay đổi:</strong> {payload.requirement.impact_warning}</div>}
          {due && <p className="deadline"><strong>Deadline:</strong> {formatTaskDeadline(due, payload.campaign.timezone, 'vi-VN')}</p>}
          {payload.task.is_overdue && <div className="notice error">Đã quá deadline theo mốc thời gian hiện tại. Trạng thái này được tính từ deadline và task state, không suy diễn lý do.</div>}
          {briefUrl && <a className="creator-link-button" href={briefUrl} target="_blank" rel="noreferrer">Mở brief v{payload.brief?.version_number ?? payload.task.requirement_version}</a>}
          {payload.task.acknowledgement_required && payload.task.acknowledgement_satisfied && (
            <p className="muted small">Đã xác nhận đúng yêu cầu v{payload.task.acknowledged_requirement_version}.</p>
          )}
          {showAcknowledge && !isPublishedOrClosed && (
            <button className="primary full-width" disabled={busy} onClick={() => void acknowledge()}>
              {busy ? 'Đang xác nhận…' : `Xác nhận yêu cầu v${payload.task.requirement_version}`}
            </button>
          )}
        </article>

        {payload.latest_review && (
          <article className={`creator-card review-card review-${payload.latest_review.decision}`}>
            <p className="eyebrow">PHẢN HỒI DRAFT MỚI NHẤT</p>
            <h2>{payload.latest_review.decision === 'revision_requested' ? 'Cần chỉnh sửa' : payload.latest_review.decision === 'approved' ? 'Draft đã được duyệt' : 'Draft không được duyệt'}</h2>
            {payload.latest_review.comment && <p>{payload.latest_review.comment}</p>}
          </article>
        )}

        {payload.publication && (
          <article className="creator-card review-card review-approved">
            <p className="eyebrow">POST LINK ĐÃ GHI NHẬN</p>
            <h2>Đã nộp link xuất bản</h2>
            <a className="creator-link-button" href={payload.publication.url} target="_blank" rel="noopener noreferrer">Mở post link</a>
            <p className="muted">Phiên bản {payload.publication.version_number} · {new Date(payload.publication.created_at).toLocaleString('vi-VN')}</p>
            {payload.publication.creator_note && <p>{payload.publication.creator_note}</p>}
          </article>
        )}

        {payload.task.acknowledgement_required && !payload.task.acknowledgement_satisfied && !isPublishedOrClosed && (
          <div className="notice">Cần xác nhận yêu cầu v{payload.task.requirement_version} trước khi nộp nội dung tiếp theo.</div>
        )}

        {canSubmitDraft && (
          <article className="creator-card">
            <p className="eyebrow">NỘP DRAFT</p>
            <h2>{payload.submission ? `Nộp draft phiên bản ${payload.submission.current_version + 1}` : 'Nộp draft đầu tiên'}</h2>
            <form className="stack" onSubmit={submitDraft}>
              <label>Loại nội dung
                <select value={kind} onChange={(event) => setKind(event.target.value as 'link' | 'text')}>
                  <option value="link">Link</option>
                  <option value="text">Nội dung văn bản</option>
                </select>
              </label>
              <label>{kind === 'link' ? 'Link draft' : 'Nội dung draft'}
                {kind === 'link'
                  ? <input type="url" required placeholder="https://..." value={content} onChange={(event) => setContent(event.target.value)} />
                  : <textarea rows={5} required value={content} onChange={(event) => setContent(event.target.value)} />}
              </label>
              <label>Ghi chú cho operator<textarea rows={3} value={note} onChange={(event) => setNote(event.target.value)} /></label>
              <button className="primary full-width" disabled={busy}>{busy ? 'Đang nộp…' : 'Nộp draft'}</button>
            </form>
          </article>
        )}

        {canPublish && (
          <article className="creator-card">
            <p className="eyebrow">NỘP POST LINK</p>
            <h2>{payload.deliverable.requires_draft ? 'Draft đã duyệt — nộp link đã đăng' : 'Nộp link đã đăng'}</h2>
            <p className="muted">Post link là phase sau approval và không tạo lại một draft chờ duyệt.</p>
            <form className="stack" onSubmit={publish}>
              <label>Link bài đã xuất bản
                <input type="url" required placeholder="https://..." value={postUrl} onChange={(event) => setPostUrl(event.target.value)} />
              </label>
              <label>Ghi chú cho operator<textarea rows={3} value={postNote} onChange={(event) => setPostNote(event.target.value)} /></label>
              <button className="primary full-width" disabled={busy}>{busy ? 'Đang ghi nhận…' : 'Nộp post link'}</button>
            </form>
          </article>
        )}

        {isPublishedOrClosed && !payload.publication && <div className="notice">Nhiệm vụ đã qua phase nộp nội dung. Liên hệ operator nếu cần mở lại.</div>}
        {message && <div className="notice success">{message}</div>}
        {error && <div className="notice error">{error}</div>}
        <button className="link-button creator-refresh" onClick={() => void load()}>Làm mới trạng thái</button>
        <footer className="creator-footer">Signed-link access · Không cần mật khẩu</footer>
      </section>
    </main>
  )
}

function CreatorState({ title, message }: { title: string; message?: string }) {
  return <main className="creator-page"><section className="creator-phone creator-state"><div className="creator-brand-mark">CO</div><h1>{title}</h1>{message && <div className="notice error">{message}</div>}</section></main>
}
